import { oc } from "@orpc/contract";
import { z } from "zod";
import { Passkey } from "./employee";

/**
 * Signing in to the admin with a passkey (WebAuthn), and the session cookie that follows. Every
 * binary WebAuthn field travels as unpadded base64url. The browser side calls
 * `navigator.credentials.create/get` with these options and sends the credential back as is.
 *
 * The session itself is an HttpOnly cookie the API sets and clears: no token ever reaches script.
 */

const Base64Url = (max: number) =>
	z
		.string()
		.max(max)
		.regex(/^[A-Za-z0-9_-]*$/);

/** The employee a session belongs to. */
export const SessionEmployee = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
});
export type SessionEmployee = z.infer<typeof SessionEmployee>;

export const CurrentSession = z.object({
	employee: SessionEmployee,
	/** When the session ends unless it is used before then, ISO 8601. */
	expiresAt: z.iso.datetime(),
});
export type CurrentSession = z.infer<typeof CurrentSession>;

const PublicKeyCredentialParameters = z.object({
	type: z.literal("public-key"),
	alg: z.number().int(),
});

const CredentialDescriptor = z.object({
	type: z.literal("public-key"),
	id: Base64Url(1400),
	transports: z.array(z.string()).optional(),
});

/** `PublicKeyCredentialCreationOptions` as JSON, for `navigator.credentials.create`. */
export const CreationOptions = z.object({
	/** Sent back with the credential, so the server finds the challenge it issued. */
	challengeId: z.string(),
	publicKey: z.object({
		rp: z.object({ id: z.string(), name: z.string() }),
		user: z.object({ id: Base64Url(128), name: z.string(), displayName: z.string() }),
		challenge: Base64Url(128),
		pubKeyCredParams: z.array(PublicKeyCredentialParameters),
		timeout: z.number().int(),
		attestation: z.literal("none"),
		authenticatorSelection: z.object({
			residentKey: z.literal("required"),
			requireResidentKey: z.literal(true),
			userVerification: z.literal("required"),
		}),
		excludeCredentials: z.array(CredentialDescriptor),
	}),
});
export type CreationOptions = z.infer<typeof CreationOptions>;

/** `PublicKeyCredentialRequestOptions` as JSON, for `navigator.credentials.get`. */
export const RequestOptions = z.object({
	challengeId: z.string(),
	publicKey: z.object({
		challenge: Base64Url(128),
		rpId: z.string(),
		timeout: z.number().int(),
		userVerification: z.literal("required"),
		/** Empty: passkeys are discoverable, the browser offers the ones it has for this site. */
		allowCredentials: z.array(CredentialDescriptor),
	}),
});
export type RequestOptions = z.infer<typeof RequestOptions>;

/** What `navigator.credentials.create` returns, serialised. */
export const RegistrationCredential = z.object({
	id: Base64Url(1400),
	rawId: Base64Url(1400),
	type: z.literal("public-key"),
	response: z.object({
		clientDataJSON: Base64Url(8_000),
		attestationObject: Base64Url(64_000),
		transports: z.array(z.string().max(32)).max(8).optional(),
	}),
});
export type RegistrationCredential = z.infer<typeof RegistrationCredential>;

/** What `navigator.credentials.get` returns, serialised. */
export const AuthenticationCredential = z.object({
	id: Base64Url(1400),
	rawId: Base64Url(1400),
	type: z.literal("public-key"),
	response: z.object({
		clientDataJSON: Base64Url(8_000),
		authenticatorData: Base64Url(8_000),
		signature: Base64Url(2_000),
		userHandle: Base64Url(128).nullable().optional(),
	}),
});
export type AuthenticationCredential = z.infer<typeof AuthenticationCredential>;

export const EnrolmentOptionsInput = z.object({
	/** The token from the enrolment link (after `#`). */
	token: z.string().min(1).max(200),
});

/** Who the enrolment link is for, with the options to create the passkey. */
export const EnrolmentOptions = CreationOptions.extend({
	employee: SessionEmployee.pick({ name: true, email: true }),
});
export type EnrolmentOptions = z.infer<typeof EnrolmentOptions>;

export const EnrolInput = z.object({
	token: z.string().min(1).max(200),
	challengeId: z.string().max(64),
	credential: RegistrationCredential,
});
export type EnrolInput = z.infer<typeof EnrolInput>;

export const LoginInput = z.object({
	challengeId: z.string().max(64),
	credential: AuthenticationCredential,
});
export type LoginInput = z.infer<typeof LoginInput>;

export const AddPasskeyInput = z.object({
	challengeId: z.string().max(64),
	credential: RegistrationCredential,
});
export type AddPasskeyInput = z.infer<typeof AddPasskeyInput>;

/**
 * An employee's password (ADR 0003): at least 12 characters, and not one from a known data breach
 * (the API checks that, answering 400 `PasswordRejected` with `breached`). Stored only as a hash.
 */
export const StaffPassword = z.string().min(12).max(256);

/** An address as typed: compared in lower case, without the spaces around it. */
const StaffEmail = z.string().trim().toLowerCase().max(254);

/** The Turnstile widget's token, when the login asks for one. */
const TurnstileToken = z.string().max(2048);

export const PasswordLoginInput = z.object({
	email: StaffEmail,
	password: z.string().min(1).max(256),
	/** Only after `BotCheckRequired`: the widget's token. */
	turnstileToken: TurnstileToken.optional(),
});
export type PasswordLoginInput = z.infer<typeof PasswordLoginInput>;

/** A code is on its way: until when it works, and when a new one can be asked for. */
export const LoginCodeSent = z.object({
	/** The pending login: send it back with the code. A secret; kept in memory only. */
	loginId: z.string(),
	expiresAt: z.iso.datetime(),
	resendAfter: z.iso.datetime(),
});
export type LoginCodeSent = z.infer<typeof LoginCodeSent>;

/**
 * The right password: signed in at once on a trusted browser (`signedIn`), otherwise a code went
 * to the employee's mailbox (`codeSent`).
 */
export const PasswordLoginResult = z.discriminatedUnion("status", [
	z.object({ status: z.literal("signedIn"), session: CurrentSession }),
	LoginCodeSent.extend({ status: z.literal("codeSent") }),
]);
export type PasswordLoginResult = z.infer<typeof PasswordLoginResult>;

export const LoginCodeInput = z.object({
	loginId: z.string().min(1).max(100),
	code: z
		.string()
		.trim()
		.regex(/^[0-9]{6}$/),
});
export type LoginCodeInput = z.infer<typeof LoginCodeInput>;

export const ResendLoginCodeInput = z.object({ loginId: z.string().min(1).max(100) });
export type ResendLoginCodeInput = z.infer<typeof ResendLoginCodeInput>;

export const RequestStaffPasswordResetInput = z.object({
	email: StaffEmail,
	turnstileToken: TurnstileToken.min(1),
});
export type RequestStaffPasswordResetInput = z.infer<typeof RequestStaffPasswordResetInput>;

export const ResetStaffPasswordInput = z.object({
	/** The token from the reset link (after `#`). */
	token: z.string().min(1).max(200),
	password: StaffPassword,
});
export type ResetStaffPasswordInput = z.infer<typeof ResetStaffPasswordInput>;

export const EnrolPasswordInput = z.object({
	token: z.string().min(1).max(200),
	password: StaffPassword,
});
export type EnrolPasswordInput = z.infer<typeof EnrolPasswordInput>;

export const SetPasswordInput = z.object({
	/** Required when the employee has a password already. */
	currentPassword: z.string().max(256).optional(),
	password: StaffPassword,
});
export type SetPasswordInput = z.infer<typeof SetPasswordInput>;

/** How the signed-in employee can sign in: "Mijn account". */
export const SignInMethods = z.object({
	passkeys: z.array(Passkey),
	password: z.object({
		set: z.boolean(),
		/** When the password last opened a session, ISO 8601. */
		lastUsedAt: z.iso.datetime().nullable(),
	}),
});
export type SignInMethods = z.infer<typeof SignInMethods>;

/** The Turnstile site key the admin's widget needs; null when Turnstile is not configured. */
export const BotCheckSettings = z.object({ siteKey: z.string().nullable() });
export type BotCheckSettings = z.infer<typeof BotCheckSettings>;

/**
 * The link in the "Wachtwoord vergeten" mail; without a token, the page that asks for one. The
 * token travels in the fragment, as the enrolment link's does.
 */
export const passwordResetUrl = (origin: string, token?: string): string =>
	token === undefined ? `${origin}/password-reset` : `${origin}/password-reset#${token}`;

export const SessionContract = {
	/** The signed-in employee; 401 without a valid session. */
	current: oc.route({ method: "GET", path: "/admin/auth/session" }).output(CurrentSession),
	/** A challenge to sign in with any of the site's passkeys. */
	loginOptions: oc.route({ method: "POST", path: "/admin/auth/login-options" }).output(RequestOptions),
	/** Checks the passkey's answer and sets the session cookie. */
	login: oc.route({ method: "POST", path: "/admin/auth/login" }).input(LoginInput).output(CurrentSession),
	/** Ends the session and clears the cookie; fine without a session too. */
	logout: oc.route({ method: "POST", path: "/admin/auth/logout" }),
	/** A challenge to attach a passkey with an enrolment link; 410 when the link is used or expired. */
	enrolmentOptions: oc
		.route({ method: "POST", path: "/admin/auth/enrolment-options" })
		.input(EnrolmentOptionsInput)
		.output(EnrolmentOptions),
	/** Stores the new passkey, uses up the link and signs the employee in. */
	enrol: oc.route({ method: "POST", path: "/admin/auth/enrolment" }).input(EnrolInput).output(CurrentSession),
	/** A challenge for the signed-in employee to add a passkey on this device. */
	passkeyOptions: oc.route({ method: "POST", path: "/admin/auth/passkey-options" }).output(CreationOptions),
	/** Stores the new passkey on the signed-in employee's account. */
	addPasskey: oc
		.route({ method: "POST", path: "/admin/auth/passkeys" })
		.input(AddPasskeyInput)
		.output(Passkey),
	/**
	 * On a Preview only: signs in with `Authorization: Bearer <personal access token>` and sets the
	 * session cookie, for the validation agent's browser. 404 anywhere else, production included.
	 */
	tokenLogin: oc.route({ method: "POST", path: "/admin/auth/token-login" }).output(CurrentSession),
	/**
	 * Signs in with email and password (ADR 0003). On a trusted browser it sets the session cookie;
	 * elsewhere it mails a code. 401 `PasswordLoginFailed` for a wrong password and an unknown
	 * address alike; 400 `BotCheckRequired` after 3 failures; 423 `LoginLocked` after 10 in 15
	 * minutes, for any address.
	 */
	passwordLogin: oc
		.route({ method: "POST", path: "/admin/auth/password-login" })
		.input(PasswordLoginInput)
		.output(PasswordLoginResult),
	/**
	 * The code from the mail: sets the session cookie and makes this browser a trusted browser for
	 * 30 days. 400 `LoginCodeWrong` with the attempts left; 410 `LoginCodeExpired` when the login
	 * must start again; 423 `LoginLocked` once wrong codes and passwords make 10.
	 */
	confirmLoginCode: oc
		.route({ method: "POST", path: "/admin/auth/password-login/code" })
		.input(LoginCodeInput)
		.output(CurrentSession),
	/**
	 * A new code, 60 seconds after the last, with the attempts the login has left; 429
	 * `LoginCodeResendTooSoon` before then.
	 */
	resendLoginCode: oc
		.route({ method: "POST", path: "/admin/auth/password-login/code/resend" })
		.input(ResendLoginCodeInput)
		.output(LoginCodeSent),
	/** "Wachtwoord vergeten": mails a reset link (1 hour) to an employee with a password. 204 for any address. */
	requestPasswordReset: oc
		.route({ method: "POST", path: "/admin/auth/password-reset", successStatus: 204 })
		.input(RequestStaffPasswordResetInput),
	/** Sets the new password; every session and trusted browser of the employee ends. */
	resetPassword: oc
		.route({ method: "POST", path: "/admin/auth/password-reset/complete", successStatus: 204 })
		.input(ResetStaffPasswordInput),
	/** Sets a password with the enrolment link, instead of a passkey, and signs the employee in. */
	enrolPassword: oc
		.route({ method: "POST", path: "/admin/auth/enrolment/password" })
		.input(EnrolPasswordInput)
		.output(CurrentSession),
	/** The signed-in employee's passkeys and password ("Mijn account"). */
	signInMethods: oc.route({ method: "GET", path: "/admin/auth/sign-in-methods" }).output(SignInMethods),
	/** Sets or changes the signed-in employee's password (a change needs the current one). */
	setPassword: oc
		.route({ method: "PUT", path: "/admin/auth/password" })
		.input(SetPasswordInput)
		.output(SignInMethods),
	/** Removes the password; 409 `LastSignInMethod` without a passkey. */
	removePassword: oc.route({ method: "DELETE", path: "/admin/auth/password" }).output(SignInMethods),
	/** The Turnstile site key for the login's widget. */
	botCheck: oc.route({ method: "GET", path: "/admin/auth/bot-check" }).output(BotCheckSettings),
};
